import { getEmbeddedAuthServerTime } from "../../../src/lib/auth/embeddedAuthClock";

type AuthToken = {
  customToken: string;
  expiresAt: string;
  serverTime?: string;
};
type Context = {
  origin: string;
  clientId: string;
  postId: string;
  sessionId: string;
};
type Session = Context & {
  dialog: HTMLDialogElement;
  frame: HTMLIFrameElement;
  focus: Element | null;
  overflow: string;
  requestId: string | null;
  closeGuard: boolean;
  closeRequestId: string | null;
};

export function createPostDialog(options: {
  getAuthProvider: () => (() => Promise<AuthToken>) | null;
  sendWidget: (message: unknown) => void;
  onClose: () => void;
}) {
  let active: Session | null = null;
  function send(session: Session, message: Record<string, unknown>) {
    session.frame.contentWindow?.postMessage(
      { ...sessionFields(session), ...message },
      session.origin,
    );
  }
  function sessionFields(session: Context) {
    return {
      clientId: session.clientId,
      postId: session.postId,
      sessionId: session.sessionId,
    };
  }
  function close(reason: "user" | "reset" | "unavailable" = "reset") {
    if (!active) return;
    const previous = active;
    active = null;
    previous.dialog.close();
    previous.dialog.remove();
    document.getElementById("nest-support-post-dialog-style")?.remove();
    document.body.style.overflow = previous.overflow;
    if (previous.focus instanceof HTMLElement && previous.focus.isConnected)
      previous.focus.focus();
    options.sendWidget({
      type: "nest-support-widget:posts:dialog:closed",
      ...sessionFields(previous),
      reason,
    });
    options.onClose();
  }
  function requestClose() {
    if (!active) return;
    if (!active.closeGuard) { close("user"); return; }
    if (active.closeRequestId) return;
    active.closeRequestId = crypto.randomUUID();
    send(active, { type: "nest-support-widget:posts:viewer:close-request", requestId: active.closeRequestId });
  }
  function open(context: Context) {
    if (active?.sessionId === context.sessionId) return;
    close();
    const dialog = document.createElement("dialog");
    dialog.id = "nest-support-post-dialog";
    dialog.setAttribute("aria-label", "お知らせ");
    const style = document.createElement("style");
    style.id = "nest-support-post-dialog-style";
    style.textContent = `#nest-support-post-dialog{position:fixed;inset:0;margin:auto;padding:0;border:0;border-radius:12px;width:min(800px,calc(100vw - 32px));max-width:none;max-height:calc(100dvh - 32px);background:#fff;color:#0f172a;box-shadow:0 20px 60px #0004;overflow:hidden}#nest-support-post-dialog::backdrop{background:#0008}#nest-support-post-dialog iframe{display:block;width:100%;height:320px;max-height:calc(100dvh - 32px);border:0}#nest-support-post-dialog button{position:absolute;right:10px;top:10px;width:36px;height:36px;border:0;border-radius:6px;background:#fff;color:#334155;font:24px sans-serif;cursor:pointer}`;
    const frame = document.createElement("iframe");
    frame.title = "ポスト本文";
    const url = new URL(
      `/embed/posts/${encodeURIComponent(context.postId)}`,
      context.origin,
    );
    url.searchParams.set("cid", context.clientId);
    url.searchParams.set("sessionId", context.sessionId);
    url.searchParams.set("parentOrigin", window.location.origin);
    frame.src = url.toString();
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = "×";
    button.setAttribute("aria-label", "お知らせを閉じる");
    button.onclick = requestClose;
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      requestClose();
    });
    dialog.addEventListener("close", () => {
      if (active?.dialog === dialog) close("user");
    });
    dialog.addEventListener("click", (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      )
        requestClose();
    });
    const session: Session = {
      ...context,
      dialog,
      frame,
      focus: document.activeElement,
      overflow: document.body.style.overflow,
      requestId: null,
      closeGuard: false,
      closeRequestId: null,
    };
    active = session;
    dialog.append(frame, button);
    document.head.append(style);
    document.body.append(dialog);
    document.body.style.overflow = "hidden";
    dialog.showModal();
  }
  async function authenticate(session: Session, messageId: string) {
    session.requestId = messageId;
    const provider = options.getAuthProvider();
    const current = () =>
      active === session &&
      session.requestId === messageId &&
      options.getAuthProvider() === provider;
    const unavailable = () =>
      send(session, {
        type: "nest-support-widget:auth-token:unavailable",
        messageId,
      });
    if (!provider) {
      unavailable();
      return;
    }
    try {
      const token = await provider();
      if (!current()) return;
      const serverTime = getEmbeddedAuthServerTime(token);
      if (
        !token.customToken ||
        !Number.isFinite(Date.parse(token.expiresAt)) ||
        serverTime === null
      ) {
        unavailable();
        return;
      }
      send(session, {
        type: "nest-support-widget:auth-token:response",
        ...token,
        messageId,
        issuedAt: new Date(serverTime).toISOString(),
      });
    } catch {
      if (current()) unavailable();
    }
  }
  function receive(event: MessageEvent): boolean {
    const session = active;
    if (
      !session ||
      event.origin !== session.origin ||
      event.source !== session.frame.contentWindow
    )
      return false;
    if (!event.data || typeof event.data !== "object") return true;
    const message = event.data as Record<string, unknown>;
    if (
      message.type === "nest-support-widget:auth-token:request" &&
      typeof message.messageId === "string" &&
      message.messageId.length > 0 &&
      message.messageId.length <= 200
    ) {
      void authenticate(session, message.messageId);
      return true;
    }
    if (
      message.sessionId !== session.sessionId ||
      message.clientId !== session.clientId ||
      message.postId !== session.postId
    )
      return true;
    switch (message.type) {
      case "nest-support-widget:posts:viewer:ready":
        session.closeGuard = message.closeGuard === true;
        send(session, {
          type: "nest-support-widget:posts:viewer:visible",
          visible: true,
        });
        break;
      case "nest-support-widget:posts:viewer:resize":
        if (
          typeof message.height === "number" &&
          Number.isFinite(message.height)
        )
          session.frame.style.height = `${Math.max(160, Math.min(10000, message.height))}px`;
        break;
      case "nest-support-widget:posts:viewer:changed":
        options.sendWidget({
          type: "nest-support-widget:posts:dialog:changed",
          ...sessionFields(session),
        });
        break;
      case "nest-support-widget:posts:viewer:close-result":
        if (session.closeRequestId && message.requestId === session.closeRequestId) {
          session.closeRequestId = null;
          if (message.allowed === true) close("user");
        }
        break;
      case "nest-support-widget:posts:viewer:close":
        requestClose();
        break;
      case "nest-support-widget:posts:viewer:unavailable":
        close("unavailable");
        break;
    }
    return true;
  }
  return {
    open,
    close,
    receive,
    isOpen: () => active !== null,
    refresh(clientId: string, sessionId: string) {
      if (active?.clientId === clientId && active.sessionId === sessionId)
        send(active, { type: "nest-support-widget:posts:viewer:refresh" });
    },
  };
}
